import {
  Box,
  CircularProgress,
  Collapse,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { Fragment, useEffect, useState } from 'react'
import { useAppDispatch } from 'store/hooks'
import { actions as dashboardActions } from 'store/dashboard'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { toTitleCase } from 'containers/user/utils'
import { MultisectorDbRow } from 'store/dashboard/types'
import ExpandToggle from '../components/ExpandToggle'
import MultisectorSearchesTable from 'containers/tables/multisectors/components/MultisectorSearchesTable'
import React from 'react'

type Props = {
  userId: string
}

interface MultisectorColumn {
  id: string
  label: string
  maxWidth?: number
  format?: (row: MultisectorDbRow) => React.ReactNode
}

export default function MultisectorsTable({ userId }: Props) {
  const dispatch = useAppDispatch()
  const data = useSelector((state: RootState) => state.dashboard.multisectorsList)
  const isLoading = useSelector((state: RootState) => state.dashboard.loading.multisectorsList)
  const error = useSelector((state: RootState) => state.dashboard.error.multisectorsList)
  const [expandedCompanyId, setExpandedCompanyId] = useState('')

  useEffect(() => {
    dispatch(dashboardActions.fetchMultisectors(userId))
  }, [dispatch, userId])

  const columns: MultisectorColumn[] = [
    {
      id: 'id',
      label: '#',
      format: row => (
        <ExpandToggle
          expanded={row.id === expandedCompanyId}
          onToggle={() => {
            setExpandedCompanyId(expandedCompanyId === row.id ? '' : row.id)
          }}
        />
      ),
    },
    {
      id: 'name',
      label: 'Name',
      format: row => (
        <Link href={`/dashboard/reporting/company/${row.id}`} target="_blank" style={{ display: 'flex', gap: 4 }}>
          <Box mt={0.2}>{toTitleCase(row.name?.split('||||')[0] || '')}</Box>
          <OpenInNewIcon sx={{ fontSize: 13 }} />
        </Link>
      ),
    },
    { id: 'status', label: 'Status' },
    {
      id: 'created_at',
      label: 'Created at',
      format: row => row.created_at.replace('.0', ''),
    },
    {
      id: 'updated_at',
      label: 'Updated at',
      format: row => row.updated_at.replace('.0', ''),
    },
    {
      id: 'startDate',
      label: 'Start date',
      format: row => row.request_body.startDate,
    },
    {
      id: 'endDate',
      label: 'End date',
      format: row => row.request_body.endDate,
    },
    {
      id: 'limit',
      label: 'Limit',
      format: row => row.request_body.limit,
    },
    {
      id: 'searches',
      label: 'Searches selected',
      maxWidth: 100,
      format: row => row.request_body.searchIds.length,
    },
  ]

  if (isLoading) {
    return (
      <Stack p={3} alignItems="center">
        <CircularProgress />
      </Stack>
    )
  }

  if (error) {
    return (
      <Paper>
        <Stack p={3} color="error.main">
          {error}
        </Stack>
      </Paper>
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            {columns.map(col => (
              <TableCell key={col.id} style={{ maxWidth: col.maxWidth }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data?.map(row => {
            const isExpanded = row.id === expandedCompanyId

            return (
              <Fragment key={row.id}>
                {/* MAIN ROW */}
                <TableRow hover>
                  {columns.map(col => (
                    <TableCell key={col.id}>{col.format ? col.format(row) : (row as any)[col.id]}</TableCell>
                  ))}
                </TableRow>

                {/* EXPANDED ROW */}
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <MultisectorSearchesTable multisectorId={row.id} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      </Table>

      {data?.length === 0 && <Stack p={2}>No multisectors created</Stack>}
    </TableContainer>
  )
}
